import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, TextField, Button, Typography, Alert, Stack, InputAdornment, IconButton,
} from '@mui/material';
import { Bolt, MailOutline, LockOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useAuth } from '../../hooks/useAuth';
import { extractApiError } from '../../api/error';

/** Map known auth failures to clear, actionable messages. */
function loginErrorMessage(err: unknown): string {
  const status = (err as { response?: { status?: number } })?.response?.status;
  const apiMsg = extractApiError(err);
  if (status === 429) return 'Too many login attempts. Please wait a few minutes and try again.';
  if (status === 403) return apiMsg || 'This account is locked or inactive. Contact an administrator.';
  if (status === 401 || !status) return 'Invalid email or password';
  return apiMsg || 'Sign in failed. Please try again.';
}

/** Branded authentication entry point. (Logic unchanged.) */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      // Land on the role-aware home ("/" → HomeRedirect): clients go to their AM dashboard.
      navigate('/', { replace: true });
    } catch (err) {
      setError(loginErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default' }}>
      {/* Brand panel */}
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
          color: '#fff',
          background: `linear-gradient(150deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.secondary.dark} 100%)`,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: alpha('#fff', 0.16), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Bolt />
          </Box>
          <Box lineHeight={1.1}>
            <Typography fontWeight={800} fontSize={20}>MyevCRM</Typography>
            <Typography fontSize={12} letterSpacing={2} textTransform="uppercase" sx={{ opacity: 0.85 }}>EV CRM</Typography>
          </Box>
        </Stack>
        <Box>
          <Typography variant="h3" fontWeight={800} sx={{ maxWidth: 520, lineHeight: 1.15 }}>
            The operations command center for EV infrastructure.
          </Typography>
          <Typography sx={{ opacity: 0.9, mt: 2, maxWidth: 480 }}>
            Projects, procurement, finance, vendors and field operations — unified, auditable, and real-time.
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>© {new Date().getFullYear()} MyevCRM. All rights reserved.</Typography>
      </Box>

      {/* Form panel */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Paper variant="outlined" sx={{ p: { xs: 3, sm: 4.5 }, width: '100%', maxWidth: 420 }}>
          <Stack spacing={0.5} mb={3}>
            <Box sx={{ display: { md: 'none' }, mb: 1 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Bolt /></Box>
            </Box>
            <Typography variant="h5">Sign in</Typography>
            <Typography variant="body2" color="text.secondary">Welcome back — please enter your details.</Typography>
          </Stack>
          <form onSubmit={onSubmit}>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth autoFocus
                InputProps={{ startAdornment: <InputAdornment position="start"><MailOutline fontSize="small" /></InputAdornment> }}
              />
              <TextField
                label="Password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockOutlined fontSize="small" /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword((s) => !s)} edge="end">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button type="submit" variant="contained" size="large" disabled={submitting} fullWidth>
                {submitting ? 'Signing in…' : 'Sign In'}
              </Button>
            </Stack>
          </form>
        </Paper>
      </Box>
    </Box>
  );
}
